import { useState } from "react";

import * as service from "../services/creatorVideoUploadService";
import { validateVideoPriceAmount } from "../utils/helper/videoPricing";

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

export const useCreatorVideoUpload = (username) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);

  const [videoUploadData, setVideoUploadData] = useState(null);
  const [thumbnailUploadData, setThumbnailUploadData] = useState(null);

  const [createdVideo, setCreatedVideo] = useState(null);

  const getCreatorVideoUploadUrl = async () => {
    try {
      setUploadingVideo(true);
      setError("");

      const res = await service.getCreatorVideoUploadUrl();
      const data = res?.data ?? null;

      setVideoUploadData(data);

      return data || false;
    } catch (requestError) {
      console.error("getCreatorVideoUploadUrl failed:", requestError);

      setVideoUploadData(null);

      setError(getErrorMessage(requestError, "Failed to prepare video upload"));

      return false;
    } finally {
      setUploadingVideo(false);
    }
  };

  const getCreatorVideoThumbnailUploadUrl = async () => {
    try {
      setUploadingThumbnail(true);
      setError("");

      const res = await service.getCreatorVideoThumbnailUploadUrl();
      const data = res?.data ?? null;

      setThumbnailUploadData(data);

      return data || false;
    } catch (requestError) {
      console.error("getCreatorVideoThumbnailUploadUrl failed:", requestError);

      setThumbnailUploadData(null);

      setError(
        getErrorMessage(requestError, "Failed to prepare thumbnail upload"),
      );

      return false;
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const addCreatorVideo = async (payload) => {
    if (!username || !payload) {
      setError(
        !username ? "Creator username is required." : "Video data is required.",
      );

      return false;
    }

    const priceValidation = validateVideoPriceAmount(
      payload.priceAmount,
      payload.videoDuration,
    );

    if (!priceValidation.valid) {
      setCreatedVideo(null);
      setError(priceValidation.message);
      return false;
    }

    try {
      setLoading(true);
      setError("");

      const res = await service.addCreatorVideo(username, {
        ...payload,
        priceAmount: priceValidation.priceAmount,
      });

      const data = res?.data ?? null;

      setCreatedVideo(data);

      return data || false;
    } catch (requestError) {
      console.error("addCreatorVideo failed:", requestError);

      setCreatedVideo(null);

      setError(getErrorMessage(requestError, "Failed to create video"));

      return false;
    } finally {
      setLoading(false);
    }
  };

  const resetCreatorVideoUpload = () => {
    setLoading(false);
    setError("");

    setUploadingVideo(false);
    setUploadingThumbnail(false);

    setVideoUploadData(null);
    setThumbnailUploadData(null);

    setCreatedVideo(null);
  };

  return {
    loading,
    error,
    uploadingVideo,
    uploadingThumbnail,
    videoUploadData,
    thumbnailUploadData,
    createdVideo,
    setError,
    addCreatorVideo,
    getCreatorVideoUploadUrl,
    getCreatorVideoThumbnailUploadUrl,
    resetCreatorVideoUpload,
  };
};

export default useCreatorVideoUpload;
